import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, DragOverEvent, DragMoveEvent } from '@dnd-kit/core'
import { db } from '../../../lib/db/indexeddb'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Task, Section } from '../../../lib/db/models'
import { EMPTY_SECTION_PREFIX } from './useSectionDrag'

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

export type OptimisticUpdates = Map<string, { order: number; sectionId?: string | null }> | null

interface UseTaskDragAndDropParams {
  segments: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  taskListRef: React.RefObject<HTMLDivElement | null>
  navSelectTask: (taskId: string) => void
  setOptimisticUpdates: Dispatch<SetStateAction<OptimisticUpdates>>
  /** When false, drags that would move a task into a different segment are rejected.
   *  Used by virtual-section pages (e.g. Quick Notes) where segments are computed
   *  from task metadata and can't be changed by reordering. */
  allowCrossSegmentDrag?: boolean
}

export function useTaskDragAndDrop({
  segments,
  tasks,
  sections,
  taskListRef,
  navSelectTask,
  setOptimisticUpdates,
  allowCrossSegmentDrag = true,
}: UseTaskDragAndDropParams) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [draggedIds, setDraggedIds] = useState<Set<string> | null>(null)
  const [dragCount, setDragCount] = useState(0)
  const [isCrossSegmentDrag, setIsCrossSegmentDrag] = useState(false)
  // Index of the segment after which a drop-gap should appear (section header shifts down).
  const [gapAfterSegmentIdx, setGapAfterSegmentIdx] = useState<number | null>(null)
  // Height of the gap (matches the over item's height + 2px list gap)
  const gapHeightRef = useRef(0)

  // Pointer Y position — updated via passive pointermove listener.
  // Used to determine whether the gap opens before or after the hovered item.
  const pointerYRef = useRef(0)
  useEffect(() => {
    const handler = (e: PointerEvent) => { pointerYRef.current = e.clientY }
    document.addEventListener('pointermove', handler, { passive: true })
    return () => document.removeEventListener('pointermove', handler)
  }, [])

  // Cross-segment gap direction: true = gap after over-item, false = gap before.
  // null = not yet determined (first handleDragOver sets it without dead zone).
  // Read by the sorting strategy in useDndComposition to decide displacement direction,
  // and by handleDragEnd to decide insertAfter.
  // The ref provides instant reads (no render delay) for the strategy function.
  // The state mirror triggers re-renders so SortableContext picks up direction changes
  // even when other state values (isCrossSegmentDrag, gapAfterSegmentIdx) stay the same.
  const crossSegGapAfterRef = useRef<boolean | null>(null)
  const [crossSegGapAfter, setCrossSegGapAfter] = useState<boolean | null>(null)

  // Ref mirrors draggedIds so handleDragEnd always reads the latest value,
  // regardless of stale closures or render timing.
  const draggedIdsRef = useRef<Set<string> | null>(null)

  // Pending IDs: set in handleDragStart, flushed to draggedIds state by useEffect.
  // This decouples the collapse animation from the first render where dnd-kit
  // measures activeNodeRect via useIsomorphicLayoutEffect. Without this, group
  // items collapse in the same render → the active item shifts up → dnd-kit
  // captures the wrong position → DragOverlay appears at item 1's position
  // instead of the grabbed item's position.
  const pendingDragIdsRef = useRef<Set<string> | null>(null)

  // Only PointerSensor — no KeyboardSensor.
  // dnd-kit's KeyboardSensor uses Enter/Space as drag-start keys, which
  // conflicts with our custom keyboard navigation (useProjectNavigation)
  // where Enter opens editing and Space toggles completion.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // Clear optimistic updates after drag completes. Delayed so useLiveQuery has
  // time to pick up the Dexie write before the optimistic layer is removed.
  // Without this delay, clearing optimistic updates could briefly revert items
  // to pre-drag order (if useLiveQuery hasn't fired yet), causing a visible jump.
  useEffect(() => {
    if (!activeDragId) {
      const timer = setTimeout(() => {
        setOptimisticUpdates(() => null)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [activeDragId, setOptimisticUpdates])

  // FLIP drop animation: after state clears, all dragged items appear at their
  // final list positions. This useLayoutEffect (before paint) starts a WAAPI
  // animation from the overlay's last position to each item's real position,
  // creating a "magnetic settle" effect. All items animate as one group —
  // they start stacked at the overlay position and fan out to their targets.
  // WAAPI runs on the compositor thread and doesn't conflict with React's
  // inline style management.
  const dropAnimRef = useRef<{ ids: string[]; x: number; y: number } | null>(null)

  useLayoutEffect(() => {
    const anim = dropAnimRef.current
    if (!anim) return
    if (activeDragId) return // Only run after drag ends
    dropAnimRef.current = null

    const wrapper = taskListRef.current
    if (!wrapper) return

    for (const id of anim.ids) {
      const el = wrapper.querySelector(`[data-task-id="${id}"]`) as HTMLElement
      if (!el) continue

      const finalRect = el.getBoundingClientRect()
      const deltaX = anim.x - finalRect.left
      const deltaY = anim.y - finalRect.top

      // Skip if negligible distance (e.g. click without movement)
      if (Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) continue

      el.animate([
        {
          transform: `translate(${deltaX}px, ${deltaY}px) scale(1.02)`,
          boxShadow: '0 12px 28px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.08)',
        },
        {
          transform: 'translate(0, 0) scale(1)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        },
      ], {
        duration: 200,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        fill: 'none',
      })
    }
  }, [activeDragId, taskListRef])

  // Deferred collapse: flush pendingDragIdsRef to state AFTER the first paint.
  // useEffect runs after paint, guaranteeing dnd-kit has already measured
  // activeNodeRect at the grabbed item's original position (before collapse).
  useEffect(() => {
    if (activeDragId && pendingDragIdsRef.current && !draggedIds) {
      const ids = pendingDragIdsRef.current
      pendingDragIdsRef.current = null
      setDraggedIds(ids)
    }
  }, [activeDragId, draggedIds])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id)

    // Check which items are currently selected via the DOM (--selected BEM modifier).
    const selectedEls = taskListRef.current?.querySelectorAll('[data-task-id]') ?? []
    const selectedIds = new Set<string>()
    selectedEls.forEach((el: Element) => {
      if (el.querySelector('.task-entry--selected')) {
        const id = el.getAttribute('data-task-id')
        if (id) selectedIds.add(id)
      }
    })

    let ids: Set<string>
    if (selectedIds.has(activeId) && selectedIds.size > 1) {
      ids = selectedIds
    } else {
      if (!selectedIds.has(activeId)) {
        navSelectTask(activeId)
      }
      ids = new Set([activeId])
    }

    // Store in ref for handleDragEnd (immune to stale closures).
    draggedIdsRef.current = ids
    // Set count immediately so DragOverlay renders the correct stack from frame 1.
    setDragCount(ids.size)
    // Trigger the overlay — dnd-kit measures activeNodeRect in useLayoutEffect.
    setActiveDragId(activeId)
    // Defer setDraggedIds to useEffect (fires AFTER paint). This ensures dnd-kit
    // measures the active item at its ORIGINAL position before group items collapse.
    pendingDragIdsRef.current = ids
  }, [navSelectTask, taskListRef])

  // Shared gap direction computation — called from handleDragOver (on over-item
  // change) and handleDragMove (on every pointer move). onDragOver only fires
  // when the over-item changes, but the pointer can cross the midpoint within
  // the same over-item. Without handleDragMove, the gap direction locks to
  // whatever it was when the item first became "over".
  const updateGapDirection = useCallback((overId: string, overSeg: number, overEl: HTMLElement | null) => {
    if (!overEl) return
    const overSegTasks = segments[overSeg]?.tasks
    if (!overSegTasks) return
    const isOverFirst = overId === overSegTasks[0]?.id
    const isOverLast = overId === overSegTasks[overSegTasks.length - 1]?.id

    const rect = overEl.getBoundingClientRect()
    const mid = rect.top + rect.height / 2
    const pointerY = pointerYRef.current
    const prev = crossSegGapAfterRef.current

    if (pointerY < rect.top) {
      // Pointer above the over-item → always gap before (first position reachable)
      crossSegGapAfterRef.current = false
    } else if (pointerY > rect.bottom) {
      // Pointer below the over-item → always gap after (last position reachable)
      crossSegGapAfterRef.current = true
    } else if (prev === null) {
      // First evaluation: set directly without dead zone
      crossSegGapAfterRef.current = pointerY >= mid
    } else if (prev && pointerY < mid - (isOverFirst ? 0 : 5)) {
      // Flip to gap-before. For the first item in a segment, no dead zone —
      // makes "first position" much easier to reach from the section header area.
      crossSegGapAfterRef.current = false
    } else if (!prev && pointerY > mid + (isOverLast ? 0 : 5)) {
      // Flip to gap-after. For the last item in a segment, no dead zone —
      // makes "last position" much easier to reach.
      crossSegGapAfterRef.current = true
    }
    // Mirror ref → state so SortableContext re-renders on direction change
    if (crossSegGapAfterRef.current !== prev) {
      setCrossSegGapAfter(crossSegGapAfterRef.current)
    }
  }, [segments])

  // Continuous gap direction tracking during drag. onDragMove fires on every
  // coordinate change (~60fps), unlike onDragOver which only fires when the
  // over-item changes. This ensures the gap direction updates when the pointer
  // crosses the midpoint of the current over-item.
  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { over } = event
    if (!over || !isCrossSegmentDrag) return
    const overId = String(over.id)
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) return

    const overSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === overId))
    if (overSeg === -1) return

    const overEl = document.querySelector(`[data-task-id="${overId}"]`) as HTMLElement
    updateGapDirection(overId, overSeg, overEl)
  }, [segments, isCrossSegmentDrag, updateGapDirection])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event
    if (!over) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }
    const activeId = String(active.id)
    const overId = String(over.id)

    // Cross-segment drags are blocked for virtual-section pages (Quick Notes).
    // Ignore empty section drop zones and over-items in other segments entirely.
    if (!allowCrossSegmentDrag && overId.startsWith(EMPTY_SECTION_PREFIX)) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }

    // Hovering over an empty section drop zone — always cross-segment
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) {
      setIsCrossSegmentDrag(true)
      setGapAfterSegmentIdx(null)
      return
    }

    const activeSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === activeId))
    const overSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === overId))
    const isCross = activeSeg !== -1 && overSeg !== -1 && activeSeg !== overSeg

    // Block cross-segment drags entirely when the flag is off — treat as no-op.
    if (isCross && !allowCrossSegmentDrag) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }

    if (!isCross) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      // Do NOT reset crossSegGapAfterRef here. When dragging from section 1
      // upward toward section 0, the pointer passes through other items in
      // section 1 first (same-segment). Resetting the ref here would erase the
      // gap direction, and re-entering cross-segment always re-initializes to
      // "gap after" (pointer below midpoint when approaching from below).
      // The ref is only meaningful during cross-segment drag and is reset
      // in handleDragStart/handleDragEnd/handleDragCancel.
      return
    }

    // ── Cross-segment drag (unified for both from-above and from-below) ──

    // 1. Determine gap direction via shared helper (also called from handleDragMove)
    const overEl = document.querySelector(`[data-task-id="${overId}"]`) as HTMLElement
    updateGapDirection(overId, overSeg, overEl)

    // 2. Source always collapses during cross-segment drag
    setIsCrossSegmentDrag(true)
    const gapAfter = crossSegGapAfterRef.current ?? true
    const itemHeight = overEl ? overEl.offsetHeight + 2 : 38
    const overSegTasks = segments[overSeg].tasks
    const isOverLast = overId === overSegTasks[overSegTasks.length - 1]?.id

    if (gapAfter && isOverLast) {
      // Last item + gap after: displacement can't shift anything.
      // CSS padding on the next segment IS the drop zone.
      gapHeightRef.current = itemHeight
      setGapAfterSegmentIdx(overSeg)
    } else if (activeSeg > overSeg) {
      // From below: padding after target compensates for source collapse
      gapHeightRef.current = itemHeight
      setGapAfterSegmentIdx(overSeg)
    } else {
      // From above: padding after source compensates for source collapse
      const activeEl = document.querySelector(`[data-task-id="${activeId}"]`) as HTMLElement
      gapHeightRef.current = activeEl ? activeEl.offsetHeight + 2 : 38
      setGapAfterSegmentIdx(activeSeg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, allowCrossSegmentDrag])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    // Read from ref for guaranteed latest value (immune to stale closures).
    const currentDraggedIds = draggedIdsRef.current
    draggedIdsRef.current = null
    pendingDragIdsRef.current = null

    if (!over || active.id === over.id || !tasks || !sections) {
      // Capture overlay's actual DOM position for FLIP settle animation.
      // Reading from the DOM is scroll-proof — initialRect + delta breaks
      // when auto-scroll shifts the viewport during drag.
      const overlayEl = document.querySelector('.drag-overlay-lift')
      if (overlayEl) {
        const overlayRect = overlayEl.getBoundingClientRect()
        const allIds = currentDraggedIds ? [...currentDraggedIds] : [String(active.id)]
        dropAnimRef.current = {
          ids: allIds,
          x: overlayRect.left,
          y: overlayRect.top,
        }
      }
      // No reorder — clear all state synchronously. With dropAnimation={null},
      // dnd-kit won't play a WAAPI animation, so no ghost issue.
      crossSegGapAfterRef.current = null
      setCrossSegGapAfter(null)
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)
      return
    }

    const activeId = String(active.id)
    const overId = String(over.id)

    // ── Drop on empty section zone ──
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) {
      const targetSectionId = overId.slice(EMPTY_SECTION_PREFIX.length)
      const targetSeg = segments.find(seg => seg.section?.id === targetSectionId)
      if (!targetSeg?.section) {
        setIsCrossSegmentDrag(false)
        setGapAfterSegmentIdx(null)
        setDraggedIds(null)
        setDragCount(0)
        setActiveDragId(null)
        return
      }
      const sectionOrder = targetSeg.section.order
      const movingIds = currentDraggedIds ?? new Set([activeId])
      const movedTasks = tasks.filter(t => movingIds.has(t.id)).sort((a, b) => a.order - b.order)

      // Place moved tasks right after the section header's order value
      // Check if next segment's section needs bumping
      const targetSegIdx = segments.indexOf(targetSeg)
      const nextSegSection = segments[targetSegIdx + 1]?.section
      const nextBoundary = nextSegSection?.order
        ?? sectionOrder + movedTasks.length + 1

      const updates: Array<{ id: string; order: number; sectionId?: string | null }> = []
      let sectionBump: { id: string; order: number } | null = null
      const step = (nextBoundary - sectionOrder) / (movedTasks.length + 1)

      movedTasks.forEach((task, i) => {
        updates.push({
          id: task.id,
          order: sectionOrder + step * (i + 1),
          sectionId: targetSectionId,
        })
      })

      // Bump next section if orders would collide
      const maxOrder = sectionOrder + step * movedTasks.length
      if (nextSegSection && nextSegSection.order <= maxOrder + 0.001) {
        sectionBump = { id: nextSegSection.id, order: Math.ceil(maxOrder) + 2 }
      }

      // FLIP animation
      const overlayEl = document.querySelector('.drag-overlay-lift')
      if (overlayEl) {
        const overlayRect = overlayEl.getBoundingClientRect()
        dropAnimRef.current = {
          ids: [...movingIds],
          x: overlayRect.left,
          y: overlayRect.top,
        }
      }

      if (updates.length > 0) {
        const optimistic = new Map<string, { order: number; sectionId?: string | null }>()
        for (const u of updates) optimistic.set(u.id, u)
        setOptimisticUpdates(optimistic)
      }

      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)

      if (updates.length > 0) {
        const writeDb = async () => {
          if (sectionBump) {
            await db.updateSection(sectionBump.id, { order: sectionBump.order })
          }
          await db.reorderTasks(updates)
          scheduleSync()
        }
        writeDb()
      }
      return
    }

    // Find target segment from the visual layout (order-based, not sectionId-based)
    const targetSegIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === overId))
    if (targetSegIdx === -1) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)
      return
    }
    const targetSeg = segments[targetSegIdx]
    const targetSectionId = targetSeg.section?.id ?? null
    const sectionOrder = targetSeg.section?.order ?? (targetSeg.tasks[0]?.order ?? 0) - 1

    const segTasks = targetSeg.tasks
    const movingIds = currentDraggedIds ?? new Set([activeId])

    // Remove moving items from segment tasks
    const remaining = segTasks.filter(t => !movingIds.has(t.id))

    // Find drop position
    const overIndex = remaining.findIndex(t => t.id === overId)
    const dropIndex = overIndex === -1 ? remaining.length : overIndex

    // Direction: insert before or after the over item.
    // Same-segment: standard direction check (dragging down = insert after).
    // Cross-segment from below: insert after (gap appears below the over item).
    const activeOrigIdx = segTasks.findIndex(t => t.id === activeId)
    const overOrigIdx = segTasks.findIndex(t => t.id === overId)
    const insertAfter = activeOrigIdx !== -1
      ? activeOrigIdx < overOrigIdx              // Same-segment: standard direction
      : (crossSegGapAfterRef.current ?? true)    // Cross-segment: from gap direction ref

    // Build new ordered list with moved items inserted
    const movedTasks = tasks.filter(t => movingIds.has(t.id)).sort((a, b) => a.order - b.order)
    const newOrder = [...remaining]
    const insertIdx = insertAfter ? dropIndex + 1 : dropIndex
    newOrder.splice(insertIdx, 0, ...movedTasks)

    // Upper bound from the next segment's section (or generous default)
    const nextSegSection = segments[targetSegIdx + 1]?.section
    const nextBoundary = nextSegSection?.order
      ?? (newOrder[newOrder.length - 1]?.order ?? sectionOrder) + newOrder.length + 1

    // Calculate new order values for moved tasks only
    const updates: Array<{ id: string; order: number; sectionId?: string | null }> = []
    let sectionBump: { id: string; order: number } | null = null
    const movedIndices = newOrder.reduce<number[]>((acc, t, i) => {
      if (movingIds.has(t.id)) acc.push(i)
      return acc
    }, [])

    if (movedIndices.length > 0) {
      const firstIdx = movedIndices[0]
      const lastIdx = movedIndices[movedIndices.length - 1]
      const prevOrder = firstIdx > 0 ? newOrder[firstIdx - 1].order : sectionOrder
      const nextOrder = lastIdx < newOrder.length - 1 ? newOrder[lastIdx + 1].order : nextBoundary

      if (prevOrder >= nextOrder) {
        // Degenerate case: renormalize entire segment
        newOrder.forEach((t, i) => {
          const newOrd = sectionOrder + i + 1
          const needsSectionUpdate = (t.sectionId ?? null) !== targetSectionId
          if (t.order !== newOrd || needsSectionUpdate) {
            updates.push({
              id: t.id,
              order: newOrd,
              ...(needsSectionUpdate ? { sectionId: targetSectionId } : {}),
            })
          }
        })
        const maxOrder = sectionOrder + newOrder.length
        if (nextSegSection && nextSegSection.order <= maxOrder + 1) {
          sectionBump = { id: nextSegSection.id, order: maxOrder + 2 }
        }
      } else {
        // Normal case: midpoint between neighbors
        const step = (nextOrder - prevOrder) / (movedIndices.length + 1)
        movedIndices.forEach((idx, i) => {
          const task = newOrder[idx]
          const needsSectionUpdate = (task.sectionId ?? null) !== targetSectionId
          updates.push({
            id: task.id,
            order: prevOrder + step * (i + 1),
            ...(needsSectionUpdate ? { sectionId: targetSectionId } : {}),
          })
        })
      }
    }

    // Capture overlay's actual DOM position for FLIP settle animation.
    // Reading from the DOM is scroll-proof — initialRect + delta breaks
    // when auto-scroll shifts the viewport during drag.
    const overlayEl = document.querySelector('.drag-overlay-lift')
    if (overlayEl) {
      const overlayRect = overlayEl.getBoundingClientRect()
      dropAnimRef.current = {
        ids: [...movingIds],
        x: overlayRect.left,
        y: overlayRect.top,
      }
    }

    // ── Synchronous state clearing ──
    // Set optimistic updates BEFORE clearing drag state so items appear at
    // their new positions in the same render as un-collapse.
    if (updates.length > 0) {
      const optimistic = new Map<string, { order: number; sectionId?: string | null }>()
      for (const u of updates) optimistic.set(u.id, u)
      setOptimisticUpdates(optimistic)
    }

    // Clear all drag state in one synchronous batch. React 18 batches these
    // with dnd-kit's internal reset into one render commit.
    // With dropAnimation={null}, there's no WAAPI animation — everything
    // happens in one frame: overlay unmounts, items un-collapse, active item visible.
    crossSegGapAfterRef.current = null
    setCrossSegGapAfter(null)
    setIsCrossSegmentDrag(false)
    setGapAfterSegmentIdx(null)
    setDraggedIds(null)
    setDragCount(0)
    setActiveDragId(null)

    // ── Async DB writes (fire-and-forget) ──
    // Visual order is already correct via optimistic updates. DB write + sync
    // happen asynchronously. useLiveQuery will catch up, then the useEffect
    // clears optimistic updates (after 100ms delay).
    if (updates.length > 0) {
      const writeDb = async () => {
        if (sectionBump) {
          await db.updateSection(sectionBump.id, { order: sectionBump.order })
        }
        await db.reorderTasks(updates)
        scheduleSync()
      }
      writeDb()
    }
  }, [tasks, sections, segments, setOptimisticUpdates])

  const handleDragCancel = useCallback(() => {
    draggedIdsRef.current = null
    pendingDragIdsRef.current = null
    crossSegGapAfterRef.current = null
    setCrossSegGapAfter(null)
    setIsCrossSegmentDrag(false)
    setGapAfterSegmentIdx(null)
    setActiveDragId(null)
    setDraggedIds(null)
    setDragCount(0)
  }, [])

  return {
    activeDragId,
    draggedIds,
    dragCount,
    isCrossSegmentDrag,
    gapAfterSegmentIdx,
    gapHeight: gapHeightRef.current,
    crossSegGapAfterRef,
    crossSegGapAfter,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  }
}
